import React, { useEffect, useState } from "react"
import { Modal, Row, Col, Label, Input } from "reactstrap"
import moment from "moment"

// Add / Edit a single statement row — shared by the bank and credit-card
// reconcile screens. `entry` null = add, otherwise edit that row. onSave gets
// { statement_date, description, reference_no, debit, credit } and resolves
// true when the save succeeded (the modal then closes).
const StatementEntryModal = ({ isOpen, toggle, entry, accountLabel, onSave }) => {
    const [date, setDate] = useState("")
    const [side, setSide] = useState("debit")
    const [amount, setAmount] = useState("")
    const [description, setDescription] = useState("")
    const [reference, setReference] = useState("")
    const [error, setError] = useState("")
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (!isOpen) return
        const isCredit = Number(entry?.credit) > 0
        setDate(entry?.statement_date ? moment(entry.statement_date).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD"))
        setSide(entry ? (isCredit ? "credit" : "debit") : "debit")
        setAmount(entry ? String(isCredit ? entry.credit : entry.debit) : "")
        setDescription(entry?.description || "")
        setReference(entry?.reference_no || "")
        setError("")
        setSaving(false)
    }, [isOpen, entry])

    const onSubmit = async () => {
        // Same rules the backend enforces — checked here first only so the
        // user gets the message without a round trip.
        if (!date) return setError("Please select a date.")
        if (date > moment().format("YYYY-MM-DD")) return setError("Date cannot be in the future.")
        if (!description.trim()) return setError("Please enter a description.")
        if (!(Number(amount) > 0)) return setError("Please enter an amount greater than zero.")
        setError("")
        setSaving(true)
        const ok = await onSave({
            statement_date: date,
            description: description.trim(),
            reference_no: reference.trim(),
            debit: side === "debit" ? Number(amount) : 0,
            credit: side === "credit" ? Number(amount) : 0,
        })
        setSaving(false)
        if (ok) toggle()
    }

    return (
        <Modal isOpen={isOpen} centered toggle={toggle} data-testid="statement-entry-modal" size="xl">
            <div className="modal-header">
                <h5 className="modal-title mt-0">{entry ? "Edit Entry" : "Add Entry"}</h5>
                <button type="button" onClick={toggle} className="btn-modal-close">
                    <span className="btn-modal-span">&times;</span>
                </button>
            </div>
            <div className="modal-body">
                {accountLabel && <div className="text-muted mb-3" style={{ fontSize: 13 }}>{accountLabel}</div>}
                <Row className="mb-3">
                    <Col md={6}>
                        <Label for="entry-date">Date</Label>
                        <Input id="entry-date" type="date" max={moment().format("YYYY-MM-DD")} value={date} onChange={e => setDate(e.target.value)} />
                    </Col>
                    <Col md={6}>
                        <Label for="entry-reference">Reference</Label>
                        <Input id="entry-reference" type="text" maxLength={100} placeholder="Cheque / UTR / ref no." value={reference} onChange={e => setReference(e.target.value)} />
                    </Col>
                </Row>
                <Row className="mb-3">
                    <Col md={12}>
                        <Label for="entry-description">Description</Label>
                        <Input id="entry-description" type="textarea" rows={2} maxLength={1000} value={description} onChange={e => setDescription(e.target.value)} />
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col md={6}>
                        <Label>Type</Label>
                        <div className="d-flex gap-3 mt-1">
                            <div>
                                <input type="radio" id="entry-side-debit" checked={side === "debit"} onChange={() => setSide("debit")} />
                                <label htmlFor="entry-side-debit" className="ms-1">Debit (money out)</label>
                            </div>
                            <div>
                                <input type="radio" id="entry-side-credit" checked={side === "credit"} onChange={() => setSide("credit")} />
                                <label htmlFor="entry-side-credit" className="ms-1">Credit (money in)</label>
                            </div>
                        </div>
                    </Col>
                    <Col md={6}>
                        <Label for="entry-amount">Amount (₹)</Label>
                        <Input id="entry-amount" type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} />
                    </Col>
                </Row>
                {error && (
                    <div className="text-danger mt-2" role="alert" style={{ fontSize: 13 }}>
                        {error}
                    </div>
                )}
            </div>
            <div className="modal-footer">
                <button className="btn btn-red btn-label me-1" onClick={toggle}>
                    <i className="dripicons-cross me-2 icon-center label-icon"></i>
                    Cancel
                </button>
                <button className="btn btn-purple btn-label" onClick={onSubmit} disabled={saving}>
                    <i className="fas fa-check me-2 icon-size label-icon"></i>
                    {entry ? "Save Changes" : "Add Entry"}
                </button>
            </div>
        </Modal>
    )
}

export default StatementEntryModal
